import React, { useEffect, useMemo, useRef, useState } from "react";
import { httpClient } from "../../HttpClient";
import { useEtatGlobal } from "../EtatGlobal";
import { IoIosSearch } from "react-icons/io";
import {
  Eye,
  Download,
  Trash2,
  Plus,
  Filter,
  Check,
  User,
  Building2,
  Loader2,
} from "lucide-react";
import { dialog } from "../../components/ui/Dialog";

// Les deux circuits d'envoi d'une LM (direct via DetailsSign.js, ou
// soumission pour validation) nomment le signataire comptable différemment
// ("Mrs Samuel" vs "Samuel HOARAU") — seul l'email reste stable, on matche
// donc dessus. Même constante que LMLead.js (carte "LM signée").
const COMPTABLE_SIGNATAIRES = {
  "samuel.hoarau@alfacomptabilite.com": "Samuel HOARAU",
  "anthony.hoarau@alfacomptabilite.com": "Anthony HOARAU",
};

const getSignataireComptable = (signers) =>
  (signers || []).find((s) =>
    Object.prototype.hasOwnProperty.call(
      COMPTABLE_SIGNATAIRES,
      (s.email || "").toLowerCase(),
    ),
  );

// Statuts réellement atteignables en fusionnant deux sources — LmValidation
// (EN_ATTENTE/REFUSE seulement : VALIDE a toujours une Envelope correspondante
// créée juste avant, voir lmValidationController.js#validerLm) et Envelope
// (LM déjà envoyée pour signature). Pas de "Brouillon"/"Validée E-C" : ces
// états n'ont pas d'équivalent durable dans les vraies données.
const STATUT_META = {
  "En cours de validation": "bg-amber-50 text-amber-700 border-amber-200",
  Refusée: "bg-red-50 text-red-700 border-red-200",
  "En signature": "bg-[#840040]/10 text-[#840040] border-[#840040]/20",
  Signée: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Annulée: "bg-slate-100 text-slate-600 border-slate-200",
  Expirée: "bg-orange-50 text-orange-700 border-orange-200",
};

function statutEnvelope(status) {
  switch (status) {
    case "sent":
    case "delivered":
      return "En signature";
    case "completed":
    case "signed":
      return "Signée";
    case "declined":
      return "Refusée";
    case "voided":
      return "Annulée";
    case "expired":
      return "Expirée";
    default:
      return status || "-";
  }
}

// Toutes les LM commencent par "LM " — inutile de le répéter sur chaque ligne.
function stripLM(type) {
  if (!type) return type;
  return type.replace(/^LM\s+/i, "").trim() || type;
}

// Même logique que LMLead.js (carte "LM signée") : le nom du dossier est la
// première partie de emailSubject ("Nom - Type"), avec repli sur nom/prénom.
function nomEnvelope(doc) {
  const name = doc.emailSubject?.split(" - ")[0]?.trim();
  if (!name || name.toLowerCase() === "n/a") {
    return `${doc.nom || ""} ${doc.prenom || ""}`.trim() || "-";
  }
  return name;
}

function typeEnvelope(doc) {
  return stripLM(doc.emailSubject?.split(" - ")[1]?.trim()) || "-";
}

function initiales(nom) {
  const mots = (nom || "").trim().split(/\s+/).filter(Boolean);
  if (mots.length === 0) return "?";
  if (mots.length === 1) return mots[0].slice(0, 2).toUpperCase();
  return (mots[0][0] + mots[1][0]).toUpperCase();
}

function dateStr(iso) {
  return new Date(iso).toLocaleDateString("fr-FR").replace(/\//g, "-");
}

const FILTRE_VIDE = { statut: [], type: [], cabinet: [] };

// Récupère le PDF en un Blob, quelle que soit la source — pour l'aperçu
// (ouvert dans un nouvel onglet) et le téléchargement, qui reposent donc sur
// exactement le même mécanisme, juste utilisé différemment ensuite. Une LM
// déjà signée ou soumise a toujours une URL directe ; une LM envoyée mais pas
// encore signée n'a pas d'URL publique, d'où le repli sur l'API DocuSign.
async function resolvePdfBlob(row) {
  if (row.source === "envelope") {
    const doc = row.raw;
    if (doc.signedPdfUrl) {
      const res = await fetch(doc.signedPdfUrl);
      if (!res.ok) throw new Error("Erreur récupération PDF signé");
      return {
        blob: await res.blob(),
        fileName: `${doc.emailSubject} - LM Signée - ${dateStr(doc.createdAt)}.pdf`,
      };
    }
    const response = await httpClient.post(
      "https://backend-myalfa.vercel.app/api/envelope/download",
      { envelopeId: doc.envelopeId },
      { responseType: "blob" },
    );
    return {
      blob: response.data,
      fileName: `${doc.emailSubject} - ${dateStr(doc.createdAt)}.pdf`,
    };
  }
  const lm = row.raw;
  const res = await fetch(lm.pdfUrl);
  if (!res.ok) throw new Error("Erreur récupération PDF");
  return {
    blob: await res.blob(),
    fileName: `${lm.documentTitre || "LM"} - ${dateStr(lm.createdAt)}.pdf`,
  };
}

function ConfirmDeleteOverlay({ nom, onCancel, onConfirm }) {
  const [saisie, setSaisie] = useState("");
  const [suppression, setSuppression] = useState(false);
  const attendu = (nom || "").trim();
  const ok = saisie.trim().length > 0 && saisie.trim() === attendu;

  const handleConfirm = async () => {
    setSuppression(true);
    await onConfirm();
    setSuppression(false);
  };

  return (
    <div
      className="fixed inset-0 bg-black/45 z-[1000] flex items-center justify-center p-4"
      onClick={onCancel}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0">
            <Trash2 className="w-4 h-4 text-red-600" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-gray-900 m-0">
              Voulez-vous vraiment supprimer cette lettre de mission ?
            </h3>
            <p className="text-xs text-gray-400 m-0 mt-0.5">Cette action est irréversible.</p>
          </div>
        </div>
        <p className="text-xs text-gray-500 mb-1.5">
          Pour confirmer, retapez le nom du client/prospect : <strong className="text-gray-800">{attendu}</strong>
        </p>
        <input
          autoFocus
          type="text"
          value={saisie}
          onChange={(e) => setSaisie(e.target.value)}
          placeholder={attendu}
          className="w-full h-9 rounded-lg border border-gray-200 px-3 text-sm focus:outline-none focus:border-[#840040] mb-4"
        />
        <div className="flex items-center justify-end gap-2">
          <button
            onClick={onCancel}
            className="px-4 py-2 rounded-lg border border-gray-200 text-gray-600 text-xs font-medium hover:bg-gray-50 transition-colors"
          >
            Annuler
          </button>
          <button
            disabled={!ok || suppression}
            onClick={handleConfirm}
            className={`px-4 py-2 rounded-lg text-xs font-semibold transition-colors ${
              ok && !suppression
                ? "bg-red-600 hover:bg-red-700 text-white"
                : "bg-gray-200 text-gray-400 cursor-not-allowed"
            }`}
          >
            {suppression ? "Suppression..." : "Supprimer"}
          </button>
        </div>
      </div>
    </div>
  );
}

function LettresMission({ onCreateLM }) {
  const { documents, fetchDocumentsLM } = useEtatGlobal();
  const [lmValidations, setLmValidations] = useState([]);
  const [loadingValidations, setLoadingValidations] = useState(true);
  const [search, setSearch] = useState("");
  const [filtres, setFiltres] = useState(FILTRE_VIDE);
  const [filterOpen, setFilterOpen] = useState(false);
  const [busyIds, setBusyIds] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null);
  const filterRef = useRef(null);

  useEffect(() => {
    setLoadingValidations(true);
    httpClient
      .get("https://backend-myalfa.vercel.app/api/lm-validations")
      .then(({ data }) => setLmValidations(Array.isArray(data) ? data : []))
      .catch((err) => console.error("Erreur récupération LM soumises :", err))
      .finally(() => setLoadingValidations(false));
  }, []);

  useEffect(() => {
    if (!filterOpen) return;
    const onDoc = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target)) setFilterOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [filterOpen]);

  const toggleFiltre = (groupe, valeur) =>
    setFiltres((f) => ({
      ...f,
      [groupe]: f[groupe].includes(valeur)
        ? f[groupe].filter((v) => v !== valeur)
        : [...f[groupe], valeur],
    }));
  const filtreCount = Object.values(filtres).reduce((n, a) => n + a.length, 0);

  const marquerOccupe = (rowId, valeur) =>
    setBusyIds((p) => ({ ...p, [rowId]: valeur }));

  const ouvrirApercu = async (row) => {
    marquerOccupe(row.id, "preview");
    try {
      const { blob } = await resolvePdfBlob(row);
      const blobUrl = window.URL.createObjectURL(blob);
      window.open(blobUrl, "_blank");
      // Pas de revoke immédiat : le nouvel onglet a besoin de l'URL vivante
      // le temps d'afficher le PDF — nettoyage différé plutôt qu'absent.
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
    } catch (err) {
      console.error("Erreur ouverture PDF :", err);
      dialog.alert("Erreur lors de l'ouverture du document.");
    } finally {
      marquerOccupe(row.id, null);
    }
  };

  const telecharger = async (row) => {
    marquerOccupe(row.id, "download");
    try {
      const { blob, fileName } = await resolvePdfBlob(row);
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("Erreur téléchargement :", err);
      dialog.alert("Erreur lors du téléchargement.");
    } finally {
      marquerOccupe(row.id, null);
    }
  };

  const supprimerEnvelope = async (id) => {
    try {
      await httpClient.delete(
        `https://backend-myalfa.vercel.app/api/delete-envelope/${id}`,
      );
      fetchDocumentsLM();
    } catch (err) {
      console.error("Erreur suppression :", err);
      dialog.alert("Erreur lors de la suppression.");
    }
  };

  const supprimerLmValidation = async (id) => {
    const avant = lmValidations;
    setLmValidations((prev) => prev.filter((lm) => lm.id !== id));
    try {
      await httpClient.delete(
        `https://backend-myalfa.vercel.app/api/lm-validations/${id}`,
      );
    } catch (err) {
      console.error("Erreur suppression :", err);
      setLmValidations(avant);
      dialog.alert("Erreur lors de la suppression.");
    }
  };

  const rows = useMemo(() => {
    const depuisEnvelopes = (documents || []).map((doc) => {
      const signataire = getSignataireComptable(doc.signers);
      return {
        id: `env-${doc.id}`,
        source: "envelope",
        raw: doc,
        reference: doc.reference || null,
        nom: nomEnvelope(doc),
        type: typeEnvelope(doc),
        kind: doc.formLeadId ? "lead" : doc.clientId ? "client" : null,
        leadRef: doc.formLeadId ? `Lead #${doc.formLeadId}` : null,
        cabinet: doc.cabinet || "-",
        collaborateur: signataire
          ? COMPTABLE_SIGNATAIRES[signataire.email.toLowerCase()]
          : null,
        statut: statutEnvelope(doc.status),
        date: doc.createdAt,
        onDelete: () => supprimerEnvelope(doc.id),
      };
    });

    const depuisValidations = (lmValidations || [])
      .filter((lm) => lm.statut !== "VALIDE")
      .map((lm) => ({
        id: `lmv-${lm.id}`,
        source: "lmValidation",
        raw: lm,
        reference: lm.reference || null,
        nom: lm.nom_client || lm.societeNom || "-",
        type: stripLM(lm.documentTitre) || "-",
        kind: lm.leadId ? "lead" : lm.clientId ? "client" : null,
        leadRef: lm.leadId ? `Lead #${lm.leadId}` : null,
        cabinet: lm.cabinet || "-",
        collaborateur: lm.comptable || null,
        statut: lm.statut === "EN_ATTENTE" ? "En cours de validation" : "Refusée",
        date: lm.createdAt,
        onDelete: () => supprimerLmValidation(lm.id),
      }));

    return [...depuisEnvelopes, ...depuisValidations].sort(
      (a, b) => new Date(b.date) - new Date(a.date),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documents, lmValidations]);

  const types = useMemo(
    () =>
      Array.from(new Set(rows.map((r) => r.type).filter((t) => t && t !== "-"))).sort(),
    [rows],
  );
  const cabinets = useMemo(
    () =>
      Array.from(new Set(rows.map((r) => r.cabinet).filter((c) => c && c !== "-"))).sort(),
    [rows],
  );

  const q = search.trim().toLowerCase();
  const filtered = rows.filter((r) => {
    if (q && !`${r.reference || ""} ${r.nom} ${r.type}`.toLowerCase().includes(q)) return false;
    if (filtres.statut.length && !filtres.statut.includes(r.statut)) return false;
    if (filtres.type.length && !filtres.type.includes(r.type)) return false;
    if (filtres.cabinet.length && !filtres.cabinet.includes(r.cabinet)) return false;
    return true;
  });

  const FILTER_GROUPS = [
    { key: "statut", label: "Statut", options: Object.keys(STATUT_META) },
    { key: "type", label: "Type de lettre de mission", options: types },
    { key: "cabinet", label: "Cabinet", options: cabinets },
  ];

  return (
    <div>
      <div className="mb-4">
        <h1 className="text-xl font-semibold text-gray-900 m-0 flex items-center gap-2">
          Lettres de mission
          <span className="bg-[#840040]/80 text-white/90 text-xs px-2 py-0.5 rounded-full font-medium">
            {rows.length}
          </span>
        </h1>
        <p className="text-xs text-gray-400 mt-1">
          Liste des lettres de mission établies par le cabinet
        </p>
      </div>

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <div className="relative flex-1 max-w-sm">
          <IoIosSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
          <input
            type="text"
            placeholder="Rechercher une référence, un dossier, un prospect..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 h-9 w-full rounded-full bg-white border border-gray-200 text-xs focus:outline-none focus:border-gray-300"
          />
        </div>
        <span className="flex-1" />
        <div className="relative" ref={filterRef}>
          <button
            onClick={() => setFilterOpen((v) => !v)}
            className={`inline-flex items-center gap-1.5 px-4 h-9 rounded-lg border text-xs font-semibold transition-colors ${
              filtreCount > 0
                ? "border-[#840040] text-[#840040] bg-[#840040]/5"
                : "border-gray-200 text-gray-600 bg-white hover:bg-gray-50"
            }`}
          >
            <Filter className="w-3.5 h-3.5" />
            Filtrer
            {filtreCount > 0 && (
              <span className="bg-[#840040] text-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center">
                {filtreCount}
              </span>
            )}
          </button>
          {filterOpen && (
            <div className="absolute right-0 top-full mt-2 w-[26rem] bg-white rounded-xl shadow-2xl border border-gray-100 p-4 z-50">
              {FILTER_GROUPS.map((groupe) => (
                <div key={groupe.key} className="mb-3">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-2">
                    {groupe.label}
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {groupe.options.length === 0 ? (
                      <p className="text-[11px] text-gray-300 italic">Aucune valeur</p>
                    ) : (
                      groupe.options.map((val) => {
                        const actif = filtres[groupe.key].includes(val);
                        return (
                          <button
                            key={val}
                            onClick={() => toggleFiltre(groupe.key, val)}
                            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-[11px] transition-colors ${
                              actif
                                ? "bg-[#840040] border-[#840040] text-white"
                                : "bg-white border-gray-200 text-gray-600 hover:border-gray-300"
                            }`}
                          >
                            {actif && <Check className="w-2.5 h-2.5" />}
                            {val}
                          </button>
                        );
                      })
                    )}
                  </div>
                </div>
              ))}
              <div className="flex items-center justify-between pt-2 border-t border-gray-100 mt-1">
                <button
                  disabled={!filtreCount}
                  onClick={() => setFiltres(FILTRE_VIDE)}
                  className="text-xs text-gray-400 hover:text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Réinitialiser
                </button>
                <button
                  onClick={() => setFilterOpen(false)}
                  className="px-4 py-1.5 rounded-lg bg-[#840040] hover:bg-[#6c0034] text-white text-xs font-semibold transition-colors"
                >
                  Appliquer
                </button>
              </div>
            </div>
          )}
        </div>
        {onCreateLM && (
          <button
            onClick={onCreateLM}
            className="inline-flex items-center gap-1.5 px-4 h-9 rounded-lg bg-[#840040] hover:bg-[#6c0034] text-white text-xs font-semibold transition-colors"
          >
            <Plus className="w-3.5 h-3.5" />
            Nouvelle lettre de mission
          </button>
        )}
      </div>

      <div className="w-full overflow-x-auto">
        <table
          className="border border-slate-200 w-full rounded-lg shadow-sm overflow-hidden bg-white"
          style={{ fontSize: "0.72rem" }}
        >
          <thead>
            <tr className="bg-gradient-to-r from-[#7A1533] to-[#5f1028]">
              <th className="px-3 py-2.5 text-white/90 font-normal text-left uppercase tracking-wide">
                Référence
              </th>
              <th className="px-3 py-2.5 text-white/90 font-normal text-left uppercase tracking-wide">
                Dossier / Prospect
              </th>
              <th className="px-3 py-2.5 text-white/90 font-normal text-left uppercase tracking-wide">
                Type de lettre
              </th>
              <th className="px-3 py-2.5 text-white/90 font-normal text-left uppercase tracking-wide">
                Statut
              </th>
              <th className="px-3 py-2.5 text-white/90 font-normal text-left uppercase tracking-wide">
                Cabinet
              </th>
              <th className="px-3 py-2.5 text-white/90 font-normal text-left uppercase tracking-wide">
                Collaborateur
              </th>
              <th className="px-3 py-2.5 text-white/90 font-normal text-left uppercase tracking-wide">
                Date de création
              </th>
              <th className="px-3 py-2.5 text-white/90 font-normal text-right uppercase tracking-wide">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loadingValidations ? (
              Array.from({ length: 8 }).map((_, i) => (
                <tr key={`skeleton-${i}`} className="animate-pulse">
                  <td className="px-3 py-2.5">
                    <div className="h-3 rounded bg-slate-100" style={{ width: 90 }} />
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full bg-[#f0d6e0] flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="h-3 rounded bg-[#f0d6e0]" style={{ width: "70%" }} />
                        <div className="h-2 mt-1.5 rounded bg-slate-100" style={{ width: "40%" }} />
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="h-3 rounded bg-slate-100" style={{ width: 90 }} />
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="h-4 rounded-full bg-slate-100" style={{ width: 100 }} />
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="h-3 rounded bg-slate-100" style={{ width: 70 }} />
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-1.5">
                      <div className="w-5 h-5 rounded-full bg-[#f0d6e0] flex-shrink-0" />
                      <div className="h-3 rounded bg-slate-100" style={{ width: 90 }} />
                    </div>
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="h-3 rounded bg-slate-100" style={{ width: 60 }} />
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center justify-end gap-1.5">
                      <div className="w-7 h-7 rounded-full bg-slate-100" />
                      <div className="w-7 h-7 rounded-full bg-slate-100" />
                      <div className="w-7 h-7 rounded-full bg-slate-100" />
                    </div>
                  </td>
                </tr>
              ))
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={8} className="py-16 text-center text-gray-400 text-xs">
                  Aucune lettre de mission ne correspond aux critères
                </td>
              </tr>
            ) : (
              filtered.map((r) => (
                <tr key={r.id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-3 py-2.5 whitespace-nowrap">
                    {r.reference ? (
                      <span className="font-mono text-[11px] font-medium text-gray-700 bg-gray-50 border border-gray-200 rounded px-1.5 py-0.5">
                        {r.reference}
                      </span>
                    ) : (
                      <span className="text-gray-300">-</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full bg-[#f5ede8] flex items-center justify-center text-[#840040] font-semibold flex-shrink-0 text-[9px]">
                        {(r.nom || "?").charAt(0).toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-gray-800 m-0 truncate">{r.nom}</p>
                        {r.kind && (
                          <p className="text-[10px] text-gray-400 m-0 flex items-center gap-1">
                            {r.kind === "lead" ? (
                              <User className="w-2.5 h-2.5" />
                            ) : (
                              <Building2 className="w-2.5 h-2.5" />
                            )}
                            {r.kind === "lead" ? r.leadRef : "Client"}
                          </p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2.5 text-gray-600 whitespace-nowrap">{r.type}</td>
                  <td className="px-3 py-2.5">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full border text-[10px] font-medium whitespace-nowrap ${
                        STATUT_META[r.statut] || "bg-gray-100 text-gray-600 border-gray-200"
                      }`}
                    >
                      {r.statut}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-gray-600 whitespace-nowrap">{r.cabinet}</td>
                  <td className="px-3 py-2.5 text-gray-600 whitespace-nowrap">
                    {r.collaborateur ? (
                      <span className="inline-flex items-center gap-1.5">
                        <span className="w-5 h-5 rounded-full bg-[#840040] text-white text-[9px] font-bold flex items-center justify-center flex-shrink-0">
                          {initiales(r.collaborateur)}
                        </span>
                        {r.collaborateur}
                      </span>
                    ) : (
                      "-"
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-gray-400 whitespace-nowrap">
                    {new Date(r.date).toLocaleDateString("fr-FR", {
                      day: "2-digit",
                      month: "2-digit",
                      year: "numeric",
                    })}
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        disabled={busyIds[r.id] === "preview"}
                        onClick={() => ouvrirApercu(r)}
                        title="Aperçu"
                        className="w-7 h-7 flex items-center justify-center rounded-full bg-[#840040]/10 text-[#840040] disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[#840040]/20 transition-colors"
                      >
                        {busyIds[r.id] === "preview" ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <Eye className="w-3.5 h-3.5" />
                        )}
                      </button>
                      <button
                        disabled={busyIds[r.id] === "download"}
                        onClick={() => telecharger(r)}
                        title="Télécharger"
                        className="w-7 h-7 flex items-center justify-center rounded-full bg-emerald-50 text-emerald-600 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-emerald-100 transition-colors"
                      >
                        {busyIds[r.id] === "download" ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <Download className="w-3.5 h-3.5" />
                        )}
                      </button>
                      <button
                        onClick={() => setDeleteTarget(r)}
                        title="Supprimer"
                        className="w-7 h-7 flex items-center justify-center rounded-full text-red-500 hover:bg-red-50 transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between gap-3 mt-3 text-[11px] text-gray-400">
        <span>
          {filtered.length} lettre{filtered.length > 1 ? "s" : ""} de mission
          {filtered.length !== rows.length ? ` sur ${rows.length}` : ""}
        </span>
      </div>

      {deleteTarget && (
        <ConfirmDeleteOverlay
          nom={deleteTarget.nom}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={async () => {
            await deleteTarget.onDelete();
            setDeleteTarget(null);
          }}
        />
      )}
    </div>
  );
}

export default LettresMission;
